import { type Step } from './types'

/**
 * Navigation storage utilities for member onboarding.
 *
 * We maintain two pieces of state in localStorage:
 * 1. Current Step - Tracks the user's current position in the flow, especially important
 *    for users returning after dropping out mid-flow
 * 2. Navigation History - Stores the sequence of visited steps (up to 10 steps),
 *    used to determine the previous step for the pages with unclear entry point (ex/ opt out)
 *
 * Storage keys are scoped by enrollment event ID to support multiple onboarding sessions.
 */

export const getNavigationStorageKeys = (enrollmentEventId: string) =>
  ({
    HISTORY: `thatch-onboarding-history-${enrollmentEventId}`,
    CURRENT_STEP: `thatch-onboarding-current-step-${enrollmentEventId}`,
  } as const)

const MAX_HISTORY_LENGTH = 10

export const getStoredHistory = (enrollmentEventId: string): Step[] => {
  try {
    const storageKeys = getNavigationStorageKeys(enrollmentEventId)
    const stored = localStorage.getItem(storageKeys.HISTORY)
    const storedArray = stored ? JSON.parse(stored) : []
    return storedArray.filter((step: Step) => step !== 'completed')
  } catch {
    return []
  }
}

export const getStoredCurrentStep = (
  enrollmentEventId: string
): Step | null => {
  try {
    const storageKeys = getNavigationStorageKeys(enrollmentEventId)
    const stored = localStorage.getItem(storageKeys.CURRENT_STEP)
    return stored && stored !== 'completed' ? (stored as Step) : null
  } catch {
    return null
  }
}

export const storeNavigation = (
  currentStep: Step,
  enrollmentEventId: string
) => {
  try {
    // Skip storing navigation for completed step and renewal detail pages
    if (
      currentStep === 'completed' ||
      currentStep.startsWith('renewal_curr_year_medical_plan_details') ||
      currentStep.startsWith('renewal_next_year_medical_plan_details')
    ) {
      return
    }

    const storageKeys = getNavigationStorageKeys(enrollmentEventId)
    localStorage.setItem(storageKeys.CURRENT_STEP, currentStep)

    let history = getStoredHistory(enrollmentEventId) || []
    // Only push the current step to history if it is not the same as previous step
    // (in case if the user is refreshing the page multiple times)
    if (history[history.length - 1] !== currentStep) {
      history.push(currentStep)
    }
    // Limit the history size since we don't want the history to grow indefinitely
    if (history.length > MAX_HISTORY_LENGTH) {
      history = history.slice(-MAX_HISTORY_LENGTH)
    }
    localStorage.setItem(storageKeys.HISTORY, JSON.stringify(history))
  } catch (error) {
    console.error('Failed to store navigation state:', error)
  }
}

export const clearOnboardingStorage = (enrollmentEventId: string) => {
  const storageKeys = getNavigationStorageKeys(enrollmentEventId)
  localStorage.removeItem(storageKeys.HISTORY)
  localStorage.removeItem(storageKeys.CURRENT_STEP)
}

/**
 * Checks if the user selected plans from SmartBudget by looking backwards through
 * navigation history to find the most recent plan selection step.
 *
 * @param enrollmentEventId - The enrollment event ID to get history for
 * @param currentStep - Optional current step to consider when checking history.
 *   If provided, we'll look at the history as if this step has already been added.
 * @returns true if the most recent plan selection step in history was smart_budget
 */
export const selectedPlansFromSmartBudget = (enrollmentEventId?: string, currentStep?: Step | null): boolean => {
  if (!enrollmentEventId) return false

  const history = getStoredHistory(enrollmentEventId)
  const planSelectionSteps = ['smart_budget', 'vision_plan', 'dental_plan', 'medical_plan']

  // If currentStep is provided and it's different from the last item in history,
  // we need to account for it as if it's already been added to history.
  // This handles the timing issue where Navigation renders before useEffect updates localStorage.
  const effectiveHistory = (currentStep && history[history.length - 1] !== currentStep)
    ? [...history, currentStep]
    : history

  // Search backwards through history (excluding current step)
  for (let i = effectiveHistory.length - 2; i >= 0; i--) {
    if (planSelectionSteps.includes(effectiveHistory[i])) {
      return effectiveHistory[i] === 'smart_budget'
    }
  }

  return false
}
